import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { GoogleMarkerService, ICreateEventMarkerOptions, MarkerAnchorPosition } from "@services/googleMarker.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { IUnplannedStop } from "@tms/_models/IUnplannedStop";
import { GeofenceService } from "@services/geofence.service";
import { PlannedRouteVisitService } from "./planned-route-visit.service";
import { PlannedRouteService } from "./planned-route.service";
import { VisitOnTimeStatus } from "../_enums/VisitOnTimeStatus";
import { GeofenceType } from "@enums/GeofenceType";
import { IRouteStop } from "@components/planning/interfaces/IRouteStop";

export interface IPlannedRouteMapMarkerOptions {
    route?: IPlannedRoute;
    unplannedStops?: IUnplannedStop[];
    showEveryStop?: boolean;
    bypassStatus?: boolean;
    showPlannedStops?: boolean;
    showUnplannedStops?: boolean;
}

export class PlannedRouteMapService {

    readonly greyColor = "#c9c9c9";
    readonly greenColor = "#60b27f";
    readonly darkColor = "#242527";
    readonly orangeColor = "#ff6e40";
    readonly redColor = "#ff4141";

    static $inject = ["veriDate", "translationService", "plannedRouteService", "plannedRouteVisitService", "googleMarkerService", "veriMarkerService", "geofenceService"];

    constructor(
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly plannedRouteVisitService: PlannedRouteVisitService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly geofenceService: GeofenceService
    ) {
    }

    createMapMarkersForRoute(options: IPlannedRouteMapMarkerOptions): IGoogleMapsMarker<IPlannedRouteVisit>[] {
        const mapMarkers: IGoogleMapsMarker<IPlannedRouteVisit>[] = [];

        if (options.showUnplannedStops) {
            this.addUnplannedStopsMarkers(options.unplannedStops, mapMarkers);
        }

        if (options.showPlannedStops) {
            this.addPlannedVisitsMarkers(options.route, mapMarkers, options?.showEveryStop, options?.bypassStatus);
        }

        return mapMarkers;
    }

    createRouteMarkerOptions(routeStop: IRouteStop, index: number, max: number): IGoogleMapsMarkerOptions {

        let icon = "---";

        let markerType = "marker-event-pause";
        //let label = `Waypoint ${index}`;
        let label = routeStop.displayLabel;

        if (index === 0) {
            icon = "play_arrow";
            label = `Start: ${label}`;
            markerType = "marker-event-start";
        }
        else if (index === max - 1) {
            icon = "stop"; //flag
            label = `End: ${label}`;
            markerType = "marker-event-end";
        }

        const options = this.googleMarkerService.createEventMarkerOptions({ label: label, markerClass: markerType, subIcon: icon, showLabel: true });

        // Bit hacky
        if (icon === "---") {
            options.labelContent = options.labelContent.replace(`<i class='material-icons marker-sub-icon'>${icon}</i>`, `<span class='marker-sub-icon text'>${index}</span>`);
        }

        options.visible = true;
        options.draggable = true;

        return options;
    }

    // ---

    private addUnplannedStopsMarkers(unplannedStops: IUnplannedStop[], existingMarkers: IGoogleMapsMarker[]): void {
        if (!unplannedStops?.length) {
            return;
        }

        unplannedStops.forEach((unplannedStop: IUnplannedStop) => {
            existingMarkers.push({
                id: Math.floor(Math.random() * 1000) + 1,
                options: this.createPipMarkerOptions(null, unplannedStop, this.darkColor, 12)
            });

            const unplannedStopTime = this.veriDate.convertMomentToTimespan(this.veriDate.createMoment(unplannedStop.VisitTime));
            const label = `${this.translationService.t("Tms.UnplannedStop")} (${unplannedStopTime})`;

            existingMarkers.push(this.createMapMarker(null, unplannedStop, {
                label: label,
                subIcon: "question_mark",
                showLabel: true,
                colour: this.greyColor
            }));
        });
    }

    private addPlannedVisitsMarkers(route: IPlannedRoute, existingMarkers: IGoogleMapsMarker[], showEveryStop: boolean = false, bypassStatus: boolean = false): void {
        if (!route?.PlannedVisits?.length) {
            return;
        }

        const showEveryNStops = (showEveryStop) ? 1 : 1; // 3; (use clustering or show every 3 stops etc)
        const showStartLocation = route.TmsRouteRules?.RouteMatchingRules?.StartRouteOnDepartStartingLocation;
        const showEndLocation = route.TmsRouteRules?.RouteMatchingRules?.EndRouteOnArriveAtEndingLocation;
        let combineStartEndLocation = false;

        const firstVisit = route.PlannedVisits[0];
        const lastVisit = route.PlannedVisits[route.PlannedVisits.length - 1];

        if (showStartLocation
            && showEndLocation
            && this.plannedRouteVisitService.hasRouteStopLocation(firstVisit)
            && this.plannedRouteVisitService.hasRouteStopLocation(lastVisit)
        ) {
            combineStartEndLocation =
                firstVisit.RouteStopLocation.Latitude === lastVisit.RouteStopLocation.Latitude &&
                firstVisit.RouteStopLocation.Longitude === lastVisit.RouteStopLocation.Longitude;
        }

        route.PlannedVisits.forEach((visit, index) => {
            if (!visit.RouteStopLocation) {
                console.warn(`No RouteStopLocation for visit index: ${index}`, visit);
                return;
            }

            let visitPlannedInFuture = new Date(visit.PlannedArrivalTime) < new Date();

            const performanceStatus: VisitOnTimeStatus = this.plannedRouteService.getArrivalVisitOnTimeStatus(visit);
            const statusColor = (visitPlannedInFuture) ? this.getColorUsingStatus(performanceStatus, bypassStatus) : this.darkColor;

            const actualArrivalTimeSpanString = (visit.ActualArrivalTime)
                ? ` (${this.veriDate.convertMomentToTimespan(this.veriDate.createMoment(visit.ActualArrivalTime))})`
                : "";
            const isFirstOrLastMarker = index === 0 || index === route.PlannedVisits.length - 1;

            let label = null;
            let icon = null;
            let color = this.greyColor;

            // first marker
            if (index === 0) {
                if (combineStartEndLocation) {
                    label = this.createAddressMarkerForVisit(visit);

                } else if (showStartLocation) {
                    label = this.createAddressMarkerForVisit(visit);

                } else {
                    label = `${index}` + actualArrivalTimeSpanString;
                }

                icon = (showStartLocation)
                    ? "home_filled"
                    : "play_arrow";

                color = (showStartLocation)
                    ? this.greyColor
                    : statusColor;

                // first visit with start location (first visit)
            } else if (showStartLocation && index === 1 && route.PlannedVisits.length > 3) {
                label = `${index}` + actualArrivalTimeSpanString;
                icon = "play_arrow";
                color = statusColor;

                // last marker with end location, different to start location
            } else if (showEndLocation && index === route.PlannedVisits.length - 1 && !combineStartEndLocation) {
                label = this.createAddressMarkerForVisit(visit);

                icon = "flag";
                color = statusColor;

                // only visit with start and end location (only visit)
            } else if (showStartLocation && showEndLocation && index === 1 && route.PlannedVisits.length === 3) {
                label = `${index}` + actualArrivalTimeSpanString;
                icon = "place";
                color = statusColor;

                // last visit with end location (last visit)
            } else if (showEndLocation && index === route.PlannedVisits.length - 2) {
                label = `${index}` + actualArrivalTimeSpanString;
                icon = "place";
                color = statusColor;

                // last marker without end location (last visit)
            } else if (!showEndLocation && index === route.PlannedVisits.length - 1) {
                label = `${index}` + actualArrivalTimeSpanString;
                icon = "flag";
                color = statusColor;

                // any stop
            } else if (index % showEveryNStops === 0) {
                label = `${index}` + actualArrivalTimeSpanString;
                icon = "place";
                color = statusColor;
            }

            const pipSize = (isFirstOrLastMarker) ? 16 : 12;

            existingMarkers.push({
                id: existingMarkers.length,
                options: this.createPipMarkerOptions(visit, null, this.darkColor, pipSize)
            });

            if (!label) {
                return;
            }

            let anchorBottom = !!route.PlannedVisits.find((plannedVisit, plannedVisitIndex) => {
                return plannedVisit.Id !== visit.Id
                    && plannedVisitIndex > index
                    && plannedVisit.RouteStopLocation
                    && plannedVisit.RouteStopLocation.Latitude === visit.RouteStopLocation.Latitude
                    && plannedVisit.RouteStopLocation.Longitude === visit.RouteStopLocation.Longitude
            });

            existingMarkers.push(this.createMapMarker(visit, null, {
                label: label,
                subIcon: icon,
                showLabel: true,
                colour: color,
                anchorPosition: (anchorBottom) ? MarkerAnchorPosition.Bottom : MarkerAnchorPosition.Top
            }));
        });
    }

    private getColorUsingStatus(status: VisitOnTimeStatus, bypassStatus: boolean = false): string {
        if (bypassStatus) {
            return this.darkColor;
        }

        switch (status) {
            case VisitOnTimeStatus.Early: return this.greenColor;
            case VisitOnTimeStatus.OnTime: return this.greenColor;
            case VisitOnTimeStatus.Late: return this.orangeColor;
            case VisitOnTimeStatus.Missed: return this.redColor;
        }
    }

    private createMapMarker(visit: IPlannedRouteVisit, unplannedStop: IUnplannedStop, options: ICreateEventMarkerOptions): IGoogleMapsMarker {
        if (!visit && !unplannedStop) {
            return null;
        }

        if (visit && (visit.RouteStopLocation.GeofenceType == GeofenceType.Polygon || (visit.RouteStopLocation.Latitude == 0 && visit.RouteStopLocation.Longitude == 0))) {
            this.geofenceService.setPolygonGeofenceVisualCenter(visit.RouteStopLocation);
        }

        options.position = {
            lat: (visit) ? visit.RouteStopLocation.Latitude : unplannedStop.Latitude,
            lng: (visit) ? visit.RouteStopLocation.Longitude : unplannedStop.Longitude
        };

        return {
            id: Math.floor(Math.random() * 1000) + 1,
            options: this.googleMarkerService.createEventMarkerOptions(options)
        };
    }

    private createPipMarkerOptions(visit: IPlannedRouteVisit, unplannedStop: IUnplannedStop, color: string, size: number = 16): IGoogleMapsMarkerOptions {
        if (!visit && !unplannedStop) {
            return null;
        }

        let options: IGoogleMapsMarkerOptions = {
            isPip: true
        };

        const icon: google.maps.Icon = {
            url: this.veriMarkerService.generateColorPip(color, size),
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
        }

        options = {
            icon: icon,
            zIndex: 0,
            position: {
                lat: (visit) ? visit.RouteStopLocation.Latitude : unplannedStop.Latitude,
                lng: (visit) ? visit.RouteStopLocation.Longitude : unplannedStop.Longitude
            }
        };

        return options;
    }

    private createAddressMarkerForVisit(visit: IPlannedRouteVisit): string {
        if (!visit) {
            return `Unknown`;
        }

        let friendlyMarkerString: string = null;

        if (visit.Name && visit.Address) {
            friendlyMarkerString = `${visit.Name}, ${visit.Address}`;
        }

        if (!friendlyMarkerString && visit.Geofence) {
            friendlyMarkerString = `${visit.Geofence.Description}`;

            if (visit.Geofence.Addr1) {
                friendlyMarkerString += `, ${visit.Geofence.Addr1}`
            }
        }

        return friendlyMarkerString;
    }

}
